// The Location → Campaign → Ad Group → Ad table, with drill-to-focus.
//
// Clicking a row re-scopes the WHOLE page to that entity and collapses the table
// to its children; the breadcrumb walks back up. Plain click drills, so there is
// no separate "isolate" mode — which means the table is both the reading surface
// and the navigation, and the attribute columns swap per level so the width goes
// to what is actually populated there.
//
// ⚠️ THE TOP LEVEL IS THE KYCK LOCATION (Phase 2c), so a shared Google account
// appears as its several real Locations rather than one lumped row. The Google
// account survives as a secondary column. Campaigns we could not attribute get
// one explicit amber node — never dropped, because a tree that silently sums to
// less than the KPI tiles above it is the worst thing this page could do.

import { useMemo } from 'react';
import { convertGoogleMetrics } from './googleCurrency';
import type { GoogleMoneyDisplay } from './googleTrendSeries';
import { AlertTriangle, ChevronRight, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { metricDef, metricSortValue } from '../../lib/googleAdsMetrics';
import {
  NOT_MEASURED,
  formatBudget,
  formatEnum,
  formatMetric,
  formatMoneyAmounts,
  formatTargetRoas,
  googleStatusRole,
} from './metricFormat';
import { LEVEL_CHILDREN_LABEL, attributeColumnsFor, levelHasImpressionShare } from './focusNav';
import {
  AD_STRENGTH_SEGMENTS,
  adStrengthDisplay,
  attributeLabel,
  compareByAttribute,
  isAttributeKey,
  shownAttributeColumns,
  type GoogleAdsAttributeKey,
} from './attributeColumns';
import { googleAccountHrefForRow } from '../../lib/googleAdsAccountLink';
import { sortRollupRows } from './rollupSort';
import type {
  GoogleAdsLevel,
  GoogleAdsMetricKey,
  GoogleAdsTreeRow,
} from '../../types/googleAdsReporting';

/**
 * What the table can be ordered by — a metric column OR an attribute column.
 * Widened from metric-only when the attribute headers became clickable.
 */
export type GoogleAdsSortKey = GoogleAdsMetricKey | GoogleAdsAttributeKey;

const STATUS_DOT: Record<string, string> = {
  active: 'bg-emerald-500',
  paused: 'bg-slate-400',
  removed: 'bg-rose-500',
  unknown: 'bg-slate-300',
};

/**
 * The delivery-status dot that sits immediately before an entity's name.
 *
 * ⚠️ Status used to be a COLUMN. It is the one attribute worth seeing on every
 * row, and it needed a whole column's width to say one word — so it moved inline,
 * where it costs nothing and cannot be toggled off. The word itself lives in the
 * tooltip: a wall of "Enabled" down a column is noise, but "which of these is
 * paused?" is answered at a glance by colour.
 *
 * Renders NOTHING where there is no Google status to show — a kyck Location and
 * the unattributed node have none, and a grey "unknown" dot on every Location row
 * would imply a state Google never reported.
 */
function StatusDot({ row }: { row: GoogleAdsTreeRow }): React.JSX.Element | null {
  if (row.level === 'location' || row.isUnattributed || !row.status) return null;
  const role = googleStatusRole(row.status);
  return (
    <span
      className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[role])}
      title={formatEnum(row.status)}
      aria-label={`Status: ${formatEnum(row.status)}`}
    />
  );
}

/**
 * Ad strength as a small segmented bar rather than a word.
 *
 * A rating on a 4-point scale reads faster as a bar than as "Average" — and it
 * lets the column be narrow. The exact word stays in the tooltip.
 */
function AdStrengthBar({ value }: { value: string | null }): React.JSX.Element {
  const { segments, fill, label, measured } = adStrengthDisplay(value);
  return (
    <span className="inline-flex items-center gap-1" title={label} aria-label={`Ad strength: ${label}`}>
      <span className="flex gap-0.5">
        {Array.from({ length: AD_STRENGTH_SEGMENTS }, (_, i) => (
          <span
            key={i}
            className={cn('h-3 w-1.5 rounded-[1px]', i < segments ? fill : 'bg-muted')}
          />
        ))}
      </span>
      {/* An unmeasured rating says so — an empty bar alone could read as "poor". */}
      {!measured && <span className="text-xs text-muted-foreground">{label}</span>}
    </span>
  );
}

/**
 * The Google Ads account a row's spend bills through — SECONDARY info since
 * Phase 2c re-keyed the tree to the kyck Location, but still what a specialist
 * needs to open the right account in Google (and to spot a shared one).
 */
function googleAccountCell(row: GoogleAdsTreeRow): React.ReactNode {
  const customers = row.googleCustomers;
  if (customers.length === 0) return NOT_MEASURED;
  if (customers.length === 1) {
    const c = customers[0]!;
    const label = c.name ?? c.id;
    // ⚠️ Only a SINGLE-customer row gets a link (googleAccountHrefForRow enforces
    // it): a Location spanning several Google accounts has no one target, and
    // sending the click to the first would open the wrong account half the time.
    const href = googleAccountHrefForRow(customers);
    if (!href) {
      return (
        <span title={`Customer id ${c.id}`} className="block max-w-[14rem] truncate">
          {label}
        </span>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title={`Open ${label} in Google Ads (customer id ${c.id})`}
        className="block max-w-[14rem] truncate text-kyck-700 hover:underline dark:text-kyck-400"
      >
        {label}
      </a>
    );
  }
  // A Location running across several Google accounts — worth saying plainly
  // rather than picking one to display.
  return (
    <span title={customers.map((c) => `${c.name ?? c.id} (${c.id})`).join('\n')}>
      {customers.length} Google accounts
    </span>
  );
}

/**
 * The ad's landing page — a truncated clickable link.
 *
 * A final URL is long and mostly boilerplate (scheme, host, tracking query), so
 * the cell shows as much as fits and puts the whole thing in the tooltip. The
 * click must not also drill the row, hence the stopPropagation — the same guard
 * the inline link icon beside the ad name uses.
 */
function finalUrlCell(row: GoogleAdsTreeRow): React.ReactNode {
  const url = (row.finalUrl ?? '').trim();
  if (url === '') return NOT_MEASURED;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={url}
      className="block max-w-[18rem] truncate text-kyck-700 hover:underline dark:text-kyck-400"
    >
      {url}
    </a>
  );
}

function attributeValue(row: GoogleAdsTreeRow, key: GoogleAdsAttributeKey): React.ReactNode {
  switch (key) {
    case 'googleAccount':
      return googleAccountCell(row);
    case 'channelType':
      return (
        <span>
          {formatEnum(row.channelType)}
          {row.channelSubType && row.channelSubType !== 'UNSPECIFIED' && (
            <span className="text-muted-foreground"> · {formatEnum(row.channelSubType)}</span>
          )}
        </span>
      );
    case 'biddingStrategyType':
      return formatEnum(row.biddingStrategyType);
    case 'targetRoas':
      return formatTargetRoas(row.targetRoas);
    case 'targetCpa':
      return row.targetCpa ? formatMoneyAmounts([row.targetCpa]) : NOT_MEASURED;
    case 'budget':
      return formatBudget(row.budgetAmount, row.budgetPeriod);
    case 'adType':
      return formatEnum(row.adType);
    case 'approvalStatus':
      return formatEnum(row.approvalStatus);
    case 'adStrength':
      return <AdStrengthBar value={row.adStrength} />;
    case 'finalUrl':
      return finalUrlCell(row);
    default:
      return NOT_MEASURED;
  }
}


/**
 * The sub-line under a CAMPAIGN row.
 *
 * Two different things used to share this slot, and only one of them earns it:
 *
 *  · The ATTRIBUTION WARNINGS (no match token / matches several locations /
 *    account not linked) are ACTIONABLE — they name a fix, and they matter
 *    because an unattributed campaign silently drops out of the table the moment
 *    any Brand / AM / Account filter is applied. These always show.
 *
 *  · The ATTRIBUTED LOCATION NAME is only worth a line when the page hasn't
 *    already said it. Under a Location node — or under a breadcrumb naming the
 *    focused Location — repeating it on every campaign row is pure redundancy
 *    costing a line of height per row. It stays only in the campaign-first case
 *    (Location toggled off, nothing focused), where there is genuinely no other
 *    indication of which client a campaign belongs to.
 *
 * The Google-account COLUMN is unaffected — that answers a different question
 * (which Google account to open), not "whose campaign is this".
 */
function CampaignSubLine({
  row,
  hasLocationContext,
}: {
  row: GoogleAdsTreeRow;
  hasLocationContext: boolean;
}): React.JSX.Element | null {
  switch (row.attributionStatus) {
    case 'unattributed':
      return (
        <div className="mt-0.5 text-xs">
          <span className="text-amber-600">No match token — unattributed</span>
        </div>
      );
    case 'collision':
      return (
        <div className="mt-0.5 text-xs">
          <span className="text-amber-600">Matches several locations — unattributed</span>
        </div>
      );
    case 'unlinked':
      return (
        <div className="mt-0.5 text-xs">
          <span className="text-amber-600">Account not linked in kyck</span>
        </div>
      );
    default:
      // Resolved (standalone / token): the Location name, but only where the page
      // isn't already showing it.
      if (hasLocationContext || !row.accountName) return null;
      return <div className="mt-0.5 text-xs text-muted-foreground">{row.accountName}</div>;
  }
}

export function GoogleAdsTree({
  rows,
  levels,
  metrics,
  attributes: selectedAttributes,
  loading,
  sortKey,
  sortDir,
  onSort,
  onDrill,
  header,
  actions,
  locationFocused = false,
  money,
}: {
  rows: GoogleAdsTreeRow[] | undefined;
  /** The levels actually nested, shallowest first. */
  levels: GoogleAdsLevel[];
  metrics: GoogleAdsMetricKey[];
  /** The attribute columns the user has turned on. Empty by default. */
  attributes: GoogleAdsAttributeKey[];
  loading: boolean;
  sortKey: GoogleAdsSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: GoogleAdsSortKey) => void;
  onDrill: (row: GoogleAdsTreeRow) => void;
  /** The level chips — rendered at the header's LEFT (the Meta layout). */
  header?: React.ReactNode;
  /** Column/view controls — rendered at the header's RIGHT. */
  actions?: React.ReactNode;
  /**
   * True when a Location is named ABOVE the table — i.e. something is focused, so
   * the breadcrumb spells it out. Combined with the Location level below.
   */
  locationFocused?: boolean;
  /** The agency display currency. Absent = native, and nothing converts. */
  money?: GoogleMoneyDisplay;
}): React.JSX.Element {
  // ⚠️ Converted BEFORE the sort, not at the cell. In display mode a money sort
  // then orders by the real converted figure rather than by `metricSortValue`'s
  // cross-currency sum — which is a defensible ordering PROXY but not the same
  // ordering, and the table would otherwise sort by one number and show another.
  // In Native mode `convertGoogleMetrics` returns each block by identity, so this
  // is a no-op and the existing behaviour is untouched.
  const converted = useMemo(
    () =>
      rows?.map((r) => ({
        ...r,
        metrics: convertGoogleMetrics(r.metrics, money?.display ?? '', money?.snapshot) ?? r.metrics,
      })),
    [rows, money?.display, money?.snapshot],
  );
  // The attribute columns actually drawn: what the user SELECTED, narrowed to
  // what the shown levels can carry. This used to be the unconditional union of
  // every shown level's attributes — they are off by default now, because most
  // of them are not wanted most of the time and they were crowding out the
  // metrics. Each still renders ONLY on its own level, so a campaign's Budget
  // cell stays blank on an ad row rather than borrowing a value that isn't the
  // ad's.
  const attributes = useMemo(
    () => shownAttributeColumns(selectedAttributes, levels),
    [selectedAttributes, levels],
  );

  // Is the Location already stated somewhere the user can see? Either as a parent
  // node in this very table, or in the breadcrumb when something is focused (every
  // focus carries a Location). Where it is, repeating it per campaign row is noise.
  const hasLocationContext = levels.includes('location') || locationFocused;

  // Impression share is campaign-grain: flag it only when a shown level can't
  // carry it, and name that level so the "—" column has an explanation.
  const shareColumns = metrics.filter((k) => metricDef(k)?.nullable);
  const levelsWithoutShare = levels.filter((l) => !levelHasImpressionShare(l));

  // ⚠️ Sorting must move SIBLINGS only — sorting the flat list would scatter
  // children away from their parents and make the parent-equals-sum invariant
  // unreadable even though it still held. `sortRollupRows` re-nests by depth.
  //
  // The key can now be a METRIC or an ATTRIBUTE. Only the comparator differs;
  // the re-nesting path is untouched, so sorting by an attribute keeps children
  // under their parents exactly as sorting by a metric always has.
  const sorted = useMemo(
    () =>
      converted
        ? sortRollupRows(converted, (a, b) => {
            if (isAttributeKey(sortKey)) return compareByAttribute(a, b, sortKey, sortDir);
            const av = metricSortValue(a.metrics, sortKey);
            const bv = metricSortValue(b.metrics, sortKey);
            // Unmeasured sorts LAST in both directions — it isn't "the smallest",
            // it's "no answer", and floating it to the top would be misleading.
            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;
            return sortDir === 'desc' ? bv - av : av - bv;
          })
        : [],
    [converted, sortKey, sortDir],
  );

  return (
    <section className="rounded-lg border border-border bg-card">
      {/* Group-by LEFT, controls RIGHT — the Meta BreakdownExplorer layout, so a
          user moving between the two dashboards finds the same control in the
          same place. */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {header}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {levels.map((l) => LEVEL_CHILDREN_LABEL[l]).join(' › ')}
            {rows && ` · ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`}
          </span>
          {actions}
        </div>
      </header>

      {levelsWithoutShare.length > 0 && shareColumns.length > 0 && (
        // Stated rather than left as a column of em dashes with no explanation.
        <p className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
          Impression share is reported at campaign level and above, so the{' '}
          {shareColumns.map((k) => metricDef(k)?.label).join(', ')}{' '}
          {shareColumns.length === 1 ? 'column shows' : 'columns show'} “—” on{' '}
          {levelsWithoutShare.map((l) => LEVEL_CHILDREN_LABEL[l].toLowerCase()).join(' and ')}.
        </p>
      )}

      {loading && !rows ? (
        <div className="space-y-2 p-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-6 w-full animate-pulse rounded bg-muted" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">Nothing matches the current filters.</p>
      ) : (
        /* ⚠️ THE MAX-HEIGHT IS WHAT MAKES THE STICKY HEADER WORK, not
           decoration. `overflow-x-auto` alone already makes this div a
           scrollport in BOTH axes (CSS computes a `visible` axis to `auto` when
           the other isn't), so a `sticky top-0` header inside it sticks to a
           container that never scrolls vertically — it scrolls away with the
           page and looks like the rule simply doesn't work. Capping the height
           gives it a scrollport that actually moves. Most tables never reach
           the cap. */
        <div className="max-h-[75vh] overflow-auto scroll-subtle">
          <table className="w-full text-sm">
            {/* Sticky at top-0 of THIS box. Nothing sticky sits above the table
                on this page, so there is no offset to apply; if a sticky filter
                bar ever lands above it, this `top-0` is what has to move. */}
            <thead className="sticky top-0 z-10 bg-card">
              {/* ⚠️ The separating line is an inset SHADOW, not the `border-b`
                  every other row uses: the table is border-collapse (Tailwind
                  preflight), and a collapsed border on a stuck header is not
                  painted — the header would lose its underline exactly when it
                  is floating over the rows and needs it most. */}
              <tr className="text-left text-xs text-muted-foreground shadow-[inset_0_-1px_0_hsl(var(--border))]">
                <th className="px-4 py-2 font-medium">Name</th>
                {/* Click-to-sort, the same affordance the metric headers have
                    always had — an attribute column you can see but not order by
                    is an odd half-column. */}
                {attributes.map((a) => (
                  <th key={a} className="whitespace-nowrap px-3 py-2 font-medium">
                    <button type="button" onClick={() => onSort(a)} className="hover:text-foreground">
                      {attributeLabel(a)}
                      {sortKey === a && (sortDir === 'desc' ? ' ↓' : ' ↑')}
                    </button>
                  </th>
                ))}
                {metrics.map((key) => (
                  <th key={key} className="whitespace-nowrap px-3 py-2 text-right font-medium">
                    <button
                      type="button"
                      onClick={() => onSort(key)}
                      className="hover:text-foreground"
                    >
                      {metricDef(key)?.label ?? key}
                      {sortKey === key && (sortDir === 'desc' ? ' ↓' : ' ↑')}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const drillable = row.level !== 'ad';
                return (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-b border-border last:border-0',
                      drillable && 'cursor-pointer hover:bg-accent/50',
                      row.isUnattributed && 'bg-amber-50/60 dark:bg-amber-950/20',
                      // A rollup subtotal reads as the group header for the rows
                      // indented beneath it.
                      row.isSubtotal && 'bg-muted/30 font-medium',
                    )}
                    onClick={drillable ? () => onDrill(row) : undefined}
                  >
                    <td className="px-4 py-2" style={{ paddingLeft: `${1 + row.depth * 1.25}rem` }}>
                      <div className="flex items-center gap-1.5">
                        {drillable && (
                          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        )}
                        {row.isUnattributed && (
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                        )}
                        {/* Delivery status, inline — no longer a column. */}
                        <StatusDot row={row} />
                        <span
                          className={cn(
                            'max-w-[22rem] truncate font-medium',
                            row.isUnattributed && 'text-amber-700 dark:text-amber-500',
                          )}
                          title={row.name}
                        >
                          {row.name}
                        </span>
                        {row.finalUrl && (
                          <a
                            href={row.finalUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-muted-foreground hover:text-foreground"
                            title={row.finalUrl}
                            // Named explicitly so it is distinguishable from the
                            // (optional) Final URL column's own link, which
                            // reads as the URL — an icon whose accessible name
                            // is a long tracking URL announces badly anyway.
                            aria-label="Open landing page"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                      {/* ⚠️ A Location shows its NAME ONLY, matching how Meta
                          renders account rows — the brand line was decorative
                          (brand is already a filter and can be a column) and cost
                          a line of height on every row. The gap node keeps its
                          sub-line because it says what to DO, and the campaign
                          attribution sub-lines below are kept for the same
                          reason: actionable, not decorative. */}
                      {row.level === 'location' && row.isUnattributed && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          These campaigns have no kyck Location — add a match token to the
                          location, or link the Google account.
                        </div>
                      )}
                      {row.level === 'campaign' && (
                        <CampaignSubLine row={row} hasLocationContext={hasLocationContext} />
                      )}
                    </td>
                    {attributes.map((a) => (
                      <td key={a} className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                        {/* An attribute renders ONLY on its native level — a
                            campaign's Budget must never appear on an ad row. */}
                        {attributeColumnsFor(row.level).includes(a) ? attributeValue(row, a) : null}

                      </td>
                    ))}
                    {metrics.map((key) => (
                      <td key={key} className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {formatMetric(row.metrics, key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
